function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06FF\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Known MAR route tokens (match MedicationCard ROUTE_OPTIONS). */
export const KNOWN_MEDICATION_ROUTES = [
  'PO',
  'IV',
  'IM',
  'SC',
  'Nebulized',
  'PR',
  'Topical',
  'Other',
  'SL',
  'ID',
  'NG',
  'PEG',
];

const ROUTE_SYNONYMS = {
  po: 'PO',
  oral: 'PO',
  orally: 'PO',
  'by mouth': 'PO',
  peroral: 'PO',
  'per os': 'PO',
  iv: 'IV',
  intravenous: 'IV',
  intravenously: 'IV',
  im: 'IM',
  intramuscular: 'IM',
  intramuscularly: 'IM',
  sc: 'SC',
  sq: 'SC',
  subcut: 'SC',
  subcutaneous: 'SC',
  subcutaneously: 'SC',
  neb: 'Nebulized',
  nebulized: 'Nebulized',
  nebulised: 'Nebulized',
  inhaled: 'Nebulized',
  inhalation: 'Nebulized',
  inhaler: 'Nebulized',
  pr: 'PR',
  rectal: 'PR',
  topical: 'Topical',
  cream: 'Topical',
  ointment: 'Topical',
  sl: 'SL',
  sublingual: 'SL',
  id: 'ID',
  intradermal: 'ID',
  ng: 'NG',
  'nasogastric': 'NG',
  peg: 'PEG',
};

/**
 * Normalize spoken/written route tokens onto MedicationCard option values.
 */
export function normalizeMedicationRoute(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const known = KNOWN_MEDICATION_ROUTES.find(
    (item) => item.toLowerCase() === raw.toLowerCase()
  );
  if (known) return known;

  const synonym = ROUTE_SYNONYMS[normalizeText(raw)];
  if (synonym) return synonym;

  return raw;
}

/**
 * Infer a clinical default route when none is stored on the order/task.
 * Always returns a non-empty MedicationCard route.
 */
export function inferDefaultMedicationRoute({
  name = '',
  drug_name = '',
  dose = '',
  itemLabel = '',
  title = '',
} = {}) {
  const haystack = normalizeText(
    [name, drug_name, dose, itemLabel, title].filter(Boolean).join(' ')
  );

  if (/\b(nebul|inhal|ventolin|salbutamol|budesonide|ipratropium|spacer)\b/.test(haystack)) {
    return 'Nebulized';
  }
  if (/\b(insulin|enoxaparin|clexane|fragmin|heparin|epoetin|filgrastim)\b/.test(haystack)) {
    return 'SC';
  }
  if (
    /\b(ceftriaxone|cefazolin|vancomycin|meropenem|piperacillin|normal saline|nacl|ringer|iv)\b/.test(
      haystack
    )
  ) {
    return 'IV';
  }
  if (/\b(cream|ointment|gel|topical|patch)\b/.test(haystack)) {
    return 'Topical';
  }
  if (/\b(suppositor|rectal|enema)\b/.test(haystack)) {
    return 'PR';
  }
  if (/\b(im|intramuscular)\b/.test(haystack)) {
    return 'IM';
  }

  return 'PO';
}

/**
 * Resolve route from explicit fields, combined dose text, then clinical default.
 * Never returns empty — MAR route must always be prefilled.
 */
export function resolveMedicationRoute(source = {}) {
  const explicit = normalizeMedicationRoute(
    source.route
      || source.metadata?.route
      || ''
  );
  if (explicit) return explicit;

  const fromDose = splitDoseAndRoute(
    source.dose || source.metadata?.dose || source.itemLabel || ''
  );
  const parsed = normalizeMedicationRoute(fromDose.route);
  if (parsed) return parsed;

  return inferDefaultMedicationRoute(source);
}

/**
 * Split combined dose+route strings used in mock/seed data
 * (e.g. "500mg IV", "1g PO", "500mg/IV") into separate fields.
 */
export function splitDoseAndRoute(text) {
  const raw = String(text || '').trim();
  if (!raw) return { dose: '', route: '' };

  const routeAlternation = KNOWN_MEDICATION_ROUTES
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((route) => route.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  const match = raw.match(
    new RegExp(`^(.*?)(?:\\s*[\\/\\-,]?\\s*|\\s+)(${routeAlternation})\\s*$`, 'i')
  );

  if (match && match[1].trim()) {
    const routeToken = match[2];
    const route =
      KNOWN_MEDICATION_ROUTES.find(
        (item) => item.toLowerCase() === routeToken.toLowerCase()
      ) || routeToken.toUpperCase();
    return { dose: match[1].trim(), route };
  }

  return { dose: raw, route: '' };
}

function drugToken(value) {
  const stop = new Set(['mg', 'g', 'mcg', 'ml', 'iv', 'po', 'im', 'sc', 'tab', 'tablet']);
  return normalizeText(value)
    .split(' ')
    .find((token) => token.length > 2 && !stop.has(token));
}

export function parseDoseFromText(text) {
  const raw = String(text || '').trim().toLowerCase();
  if (!raw) return null;

  const match = raw.match(/([\d.]+)\s*(mg|mcg|g|ml|units?)?\b/);
  if (!match) return null;

  const amount = parseFloat(match[1]);
  if (Number.isNaN(amount)) return null;

  const unit = (match[2] || 'mg').toLowerCase();
  if (unit === 'g') {
    return { amountMg: amount * 1000, display: `${amount}g` };
  }
  if (unit === 'mcg') {
    return { amountMg: amount / 1000, display: `${amount}mcg` };
  }
  if (unit === 'mg' || !match[2]) {
    return { amountMg: amount, display: `${amount}mg` };
  }

  return null;
}

export function parseTaskExpectedDose(itemLabel) {
  const label = String(itemLabel || '');
  const match = label.match(/([\d.]+)\s*(mg|mcg|g|ml)\b/i);
  if (!match) return null;
  return parseDoseFromText(match[0]);
}

export function parseMedicationDose(med) {
  if (!med) return null;
  if (med.dose_amount != null && med.dose_amount !== '') {
    const unit = String(med.dose_unit || 'mg').trim();
    return parseDoseFromText(`${med.dose_amount}${unit}`);
  }
  if (med.dose) return parseDoseFromText(med.dose);
  return null;
}

export function dosesMatch(expected, documented) {
  if (!expected || !documented) return true;
  return Math.abs(expected.amountMg - documented.amountMg) < 0.01;
}

export function medicationDrugMatchesTask(task, med) {
  const taskDrug = drugToken(task?.itemLabel || task?.title);
  const medDrug = drugToken(med?.drug_name || med?.name);
  return Boolean(taskDrug && medDrug && taskDrug === medDrug);
}

function isInTaskWindow(task, noteTime) {
  if (!task?.windowStart || !task?.windowEnd) return true;
  const start = new Date(task.windowStart);
  const end = new Date(task.windowEnd);
  if (task.status === 'overdue') {
    return noteTime >= start;
  }
  return noteTime >= start && noteTime <= end;
}

function closestTask(tasks, noteTime) {
  return [...tasks].sort((a, b) => {
    const aDue = a.dueTime ? new Date(a.dueTime).getTime() : noteTime.getTime();
    const bDue = b.dueTime ? new Date(b.dueTime).getTime() : noteTime.getTime();
    return Math.abs(aDue - noteTime.getTime()) - Math.abs(bDue - noteTime.getTime());
  })[0];
}

export function medicationEntryMatchesTask(task, med) {
  if (!medicationDrugMatchesTask(task, med)) return false;

  const expected = parseTaskExpectedDose(task.itemLabel || task.title);
  const documented = parseMedicationDose(med);
  if (expected && documented) {
    return dosesMatch(expected, documented);
  }
  return true;
}

export function getMedicationTaskDoseMismatchFlags(tasks = [], noteContent, noteTimeInput, t = null) {
  const noteTime = noteTimeInput instanceof Date ? noteTimeInput : new Date(noteTimeInput);
  const meds = noteContent?.medications_administered || noteContent?.medications || [];
  if (!meds.length || !tasks.length) return [];

  const pending = tasks.filter(
    (task) =>
      ['pending', 'overdue'].includes(task.status)
      && task.sourceNoteType === 'medication_admin'
      && isInTaskWindow(task, noteTime)
  );

  const flags = [];
  const seen = new Set();

  meds.forEach((med) => {
    const relevant = pending.filter((task) => medicationDrugMatchesTask(task, med));
    if (!relevant.length) return;

    const task = closestTask(relevant, noteTime);
    const expected = parseTaskExpectedDose(task.itemLabel || task.title);
    const documented = parseMedicationDose(med);
    if (!expected || !documented || dosesMatch(expected, documented)) return;

    const drugName = med.drug_name || med.name || task.itemLabel || 'Medication';
    const key = `${drugName}|${expected.display}|${documented.display}|${task.id}`;
    if (seen.has(key)) return;
    seen.add(key);

    flags.push({
      severity: 'verify',
      category: 'medication',
      message: t
        ? t('doseMismatchMessage', {
            documented: documented.display,
            expected: expected.display,
            taskLabel: task.itemLabel,
          })
        : `Documented dose (${documented.display}) does not match the pending task dose (${expected.display} for ${task.itemLabel}). Please confirm this is correct before saving.`,
      value: t
        ? t('doseMismatchValue', {
            drugName,
            documented: documented.display,
            expected: expected.display,
          })
        : `${drugName}: documented ${documented.display}, task expects ${expected.display}`,
    });
  });

  return flags;
}
